// RepoTracesPanel.js - Debugging panel for Q&A and Feature Builder traces
import React, { useState, useEffect, useRef } from "react";
import api, { QUERY_SEARCH_TIMEOUT_MS } from "./api";
import "./RepoTracesPanel.css";

function traceTypeLabel(trace) {
  const t = trace.traceType || (trace.phase === "cursor_prompt" ? "feature_cursor_prompt" : "feature_plan");
  if (t === "qa") return "Q&A Query Run";
  if (t === "feature_cursor_prompt") return "Cursor Prompt Generated";
  return "Feature Plan Generated";
}

function traceSummary(trace) {
  if (trace.traceType === "qa" || !trace.traceType) {
    return trace.question ? trace.question : "Q&A";
  }
  return trace.featureRequest || "Feature Builder";
}

const RepoTracesPanel = ({ repoId }) => {
  const [traces, setTraces] = useState([]);
  const [selectedTraceId, setSelectedTraceId] = useState(null);
  const [detailTrace, setDetailTrace] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(20);
  const [refreshKey, setRefreshKey] = useState(0);
  const cancelledRef = useRef(false);
  const detailCancelledRef = useRef(false);

  useEffect(() => {
    if (!repoId) return;

    cancelledRef.current = false;
    setIsLoading(true);
    setError(null);

    api
      .get(`/repos/${repoId}/traces`, {
        params: { limit },
        timeout: QUERY_SEARCH_TIMEOUT_MS,
      })
      .then((res) => {
        if (!cancelledRef.current) {
          setTraces(res.data?.traces || []);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelledRef.current) {
          console.error("Failed to load traces:", err);
          setError("Failed to load traces");
          setIsLoading(false);
        }
      });

    return () => {
      cancelledRef.current = true;
    };
  }, [repoId, limit, refreshKey]);

  useEffect(() => {
    if (!repoId || !selectedTraceId) {
      setDetailTrace(null);
      setDetailLoading(false);
      setDetailError(null);
      return;
    }

    detailCancelledRef.current = false;
    setDetailLoading(true);
    setDetailError(null);
    setDetailTrace(null);

    api
      .get(`/traces/${encodeURIComponent(selectedTraceId)}`, {
        timeout: QUERY_SEARCH_TIMEOUT_MS,
      })
      .then((res) => {
        if (!detailCancelledRef.current) {
          setDetailTrace(res.data?.trace || null);
          setDetailLoading(false);
        }
      })
      .catch((err) => {
        if (!detailCancelledRef.current) {
          console.error("Failed to load trace detail:", err);
          setDetailError("Failed to load trace details");
          setDetailLoading(false);
        }
      });

    return () => {
      detailCancelledRef.current = true;
    };
  }, [repoId, selectedTraceId]);

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleString();
    } catch (e) {
      return dateString;
    }
  };

  const truncateText = (text, maxLength = 80) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const isFeatureTrace = (trace) =>
    trace.traceType === "feature_plan" || trace.traceType === "feature_cursor_prompt";

  if (!repoId) {
    return <div className="traces-panel">No repository selected</div>;
  }

  return (
    <div className="traces-panel">
      <div className="traces-toolbar">
        <div className="toolbar-group">
          <label htmlFor="limit-select">Limit:</label>
          <select
            id="limit-select"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="limit-select"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
        <button onClick={handleRefresh} className="refresh-btn">
          Refresh
        </button>
      </div>

      {isLoading && <div className="traces-loading">Loading traces...</div>}
      {error && <div className="traces-error">{error}</div>}
      {!isLoading && !error && traces.length === 0 && (
        <div className="traces-empty">No traces found for this repo yet.</div>
      )}

      {!isLoading && !error && traces.length > 0 && (
        <div className="traces-layout">
          <div className="traces-table-container">
            <table className="traces-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Summary</th>
                  <th>Cache</th>
                  <th>Total (ms)</th>
                  <th>Chunks / Files</th>
                  <th>Tokens</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {traces.map((trace) => (
                  <tr
                    key={trace.traceId || trace._id}
                    onClick={() => setSelectedTraceId(trace.traceId || null)}
                    className={`trace-row ${
                      selectedTraceId && trace.traceId === selectedTraceId
                        ? "selected"
                        : ""
                    }`}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <span className={`trace-type-badge trace-type-${trace.traceType === "feature_cursor_prompt" || trace.phase === "cursor_prompt" ? "feature_cursor_prompt" : trace.traceType === "feature_plan" || trace.phase === "plan_generation" ? "feature_plan" : "qa"}`}>
                        {traceTypeLabel(trace)}
                      </span>
                    </td>
                    <td className="question-cell">
                      {truncateText(traceSummary(trace), 80)}
                    </td>
                    <td>{trace.fromCache != null ? (trace.fromCache ? "YES" : "NO") : "—"}</td>
                    <td>{trace.timing?.totalMs ?? "—"}</td>
                    <td>
                      {trace.chunksCount != null
                        ? trace.chunksCount
                        : trace.context?.chunks != null
                          ? trace.context.chunks.length
                          : trace.selectedFiles?.length ?? trace.candidateFiles?.length ?? "—"}
                    </td>
                    <td>{trace.openaiResponse?.totalTokens ?? "—"}</td>
                    <td>{formatDate(trace.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="traces-detail-container">
            {!selectedTraceId ? (
              <div className="traces-detail-placeholder">
                Select a trace from the table to view details
              </div>
            ) : detailLoading ? (
              <div className="traces-loading">Loading trace details…</div>
            ) : detailError ? (
              <div className="traces-error">{detailError}</div>
            ) : detailTrace ? (
              isFeatureTrace(detailTrace) ? (
                <FeatureTraceDetail trace={detailTrace} />
              ) : (
                <QaTraceDetail trace={detailTrace} />
              )
            ) : (
              <div className="traces-detail-placeholder">No detail available.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function QaTraceDetail({ trace }) {
  return (
    <div className="traces-detail">
      <h3>Trace Details</h3>
      <span className={`trace-type-badge trace-type-qa`}>{traceTypeLabel(trace)}</span>
      <div className="detail-section">
        <h4>Question</h4>
        <p className="detail-text">{trace.question}</p>
      </div>
      <div className="detail-section">
        <h4>Answer Preview</h4>
        <p className="detail-text">
          {trace.answerPreview || trace.answer || "No answer available"}
        </p>
      </div>
      <div className="detail-section">
        <h4>Timing</h4>
        <pre className="detail-json">
          {JSON.stringify(trace.timing || {}, null, 2)}
        </pre>
      </div>
      <div className="detail-section">
        <h4>Context Summary</h4>
        <p>
          <strong>Chunks:</strong>{" "}
          {trace.context?.chunks?.length ?? trace.chunksCount ?? 0}
        </p>
        <p>
          <strong>Flows:</strong>{" "}
          {trace.context?.flows?.length ?? trace.flowsCount ?? 0}
        </p>
        <p><strong>Architecture Overview Included:</strong> {trace.context?.architectureOverviewIncluded ? "Yes" : "No"}</p>
        <p><strong>Conversation History:</strong> {trace.context?.conversationHistoryCount || 0} messages</p>
        <details>
          <summary>Metadata Summary</summary>
          <pre className="detail-json">{JSON.stringify(trace.context?.metadataSummary || {}, null, 2)}</pre>
        </details>
      </div>
      <div className="detail-section">
        <h4>Token Usage</h4>
        <pre className="detail-json">{JSON.stringify(trace.openaiResponse || {}, null, 2)}</pre>
      </div>
      {trace.errors?.length > 0 && (
        <div className="detail-section">
          <h4>Errors</h4>
          <pre className="detail-json detail-json-error">{JSON.stringify(trace.errors, null, 2)}</pre>
        </div>
      )}
      <div className="detail-section">
        <h4>Full Trace (JSON)</h4>
        <details>
          <summary>Expand to see full trace object</summary>
          <pre className="detail-json">{JSON.stringify(trace, null, 2)}</pre>
        </details>
      </div>
    </div>
  );
}

function FeatureTraceDetail({ trace }) {
  const [expandedPrompts, setExpandedPrompts] = useState({});
  const artifacts = trace.promptArtifacts;
  const hasArtifacts = artifacts && (
    (artifacts.cursorPrompt?.text) ||
    (artifacts.planInputPrompt?.text) ||
    (artifacts.intentPrompt?.text)
  );

  const togglePrompt = (key) => {
    setExpandedPrompts((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="traces-detail">
      <h3>Feature Builder Trace</h3>
      <span className={`trace-type-badge trace-type-${trace.traceType}`}>
        {traceTypeLabel(trace)}
      </span>
      <div className="detail-section">
        <h4>Feature Request</h4>
        <p className="detail-text">{trace.featureRequest || "—"}</p>
      </div>
      <div className="detail-section">
        <h4>Event</h4>
        <p>{traceTypeLabel(trace)}</p>
      </div>
      <div className="detail-section">
        <h4>Summary</h4>
        <p><strong>Candidate files:</strong> {trace.candidateFiles?.length ?? trace.planQualitySignals?.candidateFiles ?? "—"}</p>
        <p><strong>Selected files:</strong> {trace.selectedFiles?.length ?? trace.planQualitySignals?.selectedFiles ?? "—"}</p>
        <p><strong>Plan steps:</strong> {trace.planStepsCount ?? trace.planQualitySignals?.planSteps ?? "—"}</p>
        <p><strong>Cursor prompt generated:</strong> {trace.cursorPromptGenerated ? "Yes" : "No"}</p>
      </div>
      {trace.selectedFiles?.length > 0 && (
        <div className="detail-section">
          <h4>Selected Files</h4>
          <ul className="detail-file-list">
            {trace.selectedFiles.slice(0, 30).map((p) => (
              <li key={p}>{p}</li>
            ))}
            {trace.selectedFiles.length > 30 && <li>… and {trace.selectedFiles.length - 30} more</li>}
          </ul>
        </div>
      )}
      <div className="detail-section">
        <h4>Timing</h4>
        <pre className="detail-json">{JSON.stringify(trace.timing || {}, null, 2)}</pre>
      </div>
      <div className="detail-section">
        <h4>Prompt content</h4>
        {!hasArtifacts ? (
          <p className="detail-text muted">No stored prompt content for this trace.</p>
        ) : (
          <>
            {artifacts.cursorPrompt?.text && (
              <div className="prompt-artifact">
                <button type="button" className="prompt-artifact-toggle" onClick={() => togglePrompt("cursorPrompt")}>
                  {expandedPrompts.cursorPrompt ? "Collapse" : "Expand"} Cursor Prompt
                  {artifacts.cursorPrompt.truncated && " (truncated)"}
                </button>
                {expandedPrompts.cursorPrompt && (
                  <pre className="detail-json prompt-artifact-body">{artifacts.cursorPrompt.text}</pre>
                )}
              </div>
            )}
            {artifacts.planInputPrompt?.text && (
              <div className="prompt-artifact">
                <button type="button" className="prompt-artifact-toggle" onClick={() => togglePrompt("planInputPrompt")}>
                  {expandedPrompts.planInputPrompt ? "Collapse" : "Expand"} Plan Input Prompt
                  {artifacts.planInputPrompt.truncated && " (truncated)"}
                </button>
                {expandedPrompts.planInputPrompt && (
                  <pre className="detail-json prompt-artifact-body">{artifacts.planInputPrompt.text}</pre>
                )}
              </div>
            )}
            {artifacts.intentPrompt?.text && (
              <div className="prompt-artifact">
                <button type="button" className="prompt-artifact-toggle" onClick={() => togglePrompt("intentPrompt")}>
                  {expandedPrompts.intentPrompt ? "Collapse" : "Expand"} Intent Prompt
                  {artifacts.intentPrompt.truncated && " (truncated)"}
                </button>
                {expandedPrompts.intentPrompt && (
                  <pre className="detail-json prompt-artifact-body">{artifacts.intentPrompt.text}</pre>
                )}
              </div>
            )}
          </>
        )}
      </div>
      {trace.traceErrors?.length > 0 && (
        <div className="detail-section">
          <h4>Errors</h4>
          <pre className="detail-json detail-json-error">{JSON.stringify(trace.traceErrors, null, 2)}</pre>
        </div>
      )}
      <div className="detail-section">
        <h4>Full Trace (JSON)</h4>
        <details>
          <summary>Expand to see full trace object</summary>
          <pre className="detail-json">{JSON.stringify(trace, null, 2)}</pre>
        </details>
      </div>
    </div>
  );
}

export default RepoTracesPanel;

